import { useEffect, useMemo } from "react";

import { getServiceIcon } from "Components/ServiceIcon/helper";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { currentDeployment as currentDeploymentSelector } from "Reducers/environment/deployment";
import { servicesSelector } from "Reducers/environment/service/service.selectors";
import { getAutoscalingConfigurationThunk } from "Reducers/environment/service/service.slice";
import { gitProjectSelector } from "Reducers/project/project";
import {
  getDeploymentRouter,
  getDeploymentWebApps,
  getDeploymentServices,
  getDeploymentWorkers
} from "src/organization/common/containers/ServicesDisplay/util/serviceGraph";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import type { DeploymentServices, DeploymentService } from "@packages/client";
import type { Tree } from "src/organization/common/containers/ServicesDisplay/util/serviceGraph";

const sanitizeId = (id: string): string => id.replace(/^(\d)/gi, "node-$1");

const isTweenService = (service?: { type?: string }) =>
  service?.type?.split(":", 1)[0] === "varnish";

const webAppIndex = (listOfWebApps: DeploymentService[], serviceName: string) =>
  listOfWebApps.findIndex(nestedRelationship => {
    const relationship = Object.values(
      nestedRelationship.relationships ?? {}
    )[0];

    const endpointName =
      typeof relationship === "string"
        ? relationship.split(":", 1)[0]
        : relationship?.service;
    return endpointName === serviceName;
  });

const sortedServicesBasedOnWebApp = (
  webapps: DeploymentServices,
  services: DeploymentServices
) => {
  const listOfWebApps = Object.values(webapps);

  return Object.entries(services ?? {}).sort((a, b) => {
    const [serviceNameA] = a;
    const [serviceNameB] = b;

    const indexOfWebAppForServiceA = webAppIndex(listOfWebApps, serviceNameA);
    const indexOfWebAppForServiceB = webAppIndex(listOfWebApps, serviceNameB);

    if (indexOfWebAppForServiceA < indexOfWebAppForServiceB) return -1;
    if (indexOfWebAppForServiceA > indexOfWebAppForServiceB) return 1;
    return 0;
  });
};

export const useServiceGraphTreeData = () => {
  const { environmentId, projectId } = useDecodedParams<{
    environmentId?: string;
    projectId: string;
  }>();
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const dispatch = useAppDispatch();

  const envId = environmentId || project?.default_branch;

  const autoscalingConfiguration = useAppSelector(servicesSelector);

  useEffect(() => {
    if (projectId && environmentId) {
      dispatch(getAutoscalingConfigurationThunk({ projectId, environmentId }));
    }
  }, [dispatch, projectId, environmentId]);

  const currentDeployment = useSelectorWithUrlParams(
    currentDeploymentSelector,
    { environmentId: envId }
  );
  const serviceGraphTree = useMemo(() => {
    if (!currentDeployment) {
      return [];
    }

    const treeGraph: Tree = [];

    const router = getDeploymentRouter(currentDeployment);

    Object.values(currentDeployment.routes ?? {}).forEach(route => {
      if (route.type !== "upstream") {
        return;
      }

      const targetName = route?.upstream?.split(":", 1)[0];

      if (targetName) {
        router.children.push(targetName);
      }
    });

    treeGraph.push(router);

    // Router is in line 0
    let currentLine = 1;
    // Check if we have a varnish service
    // Varnish is a special case of services that sit between
    // routes and the app.
    const isTweenServices = Object.entries(
      currentDeployment.services ?? {}
    ).some(([, s]) => isTweenService(s));
    if (isTweenServices) {
      // We put the app in line 2, service in line 3, worker in line 4
      currentLine++;
    }

    Object.entries(currentDeployment.webapps ?? {}).forEach(
      ([name, app], i) => {
        const { iconName } = getServiceIcon(app);
        const a = getDeploymentWebApps({
          name,
          iconName,
          currentLine,
          column: i,
          app,
          autoscalingConfig: autoscalingConfiguration,
          environmentId
        });

        Object.entries({ ...app.relationships, ...app.mounts }).forEach(
          ([, endpoint]) => {
            let targetName;
            if (endpoint.source) {
              if (endpoint.source !== "service") {
                return false;
              }

              targetName = endpoint.service;
            } else if (typeof endpoint === "string") {
              targetName = endpoint?.split?.(":", 1)[0];
            } else {
              targetName = endpoint.service;
            }

            a.children.push(targetName);
          }
        );

        treeGraph.push(a);
      }
    );

    currentLine++;

    sortedServicesBasedOnWebApp(
      currentDeployment.webapps,
      currentDeployment.services
    ).forEach(([name, service], i) => {
      const type = service?.type.split(":", 1)[0];

      const isTween = isTweenService(service);

      const s = getDeploymentServices({
        name,
        type,
        currentLine: isTween ? 1 : currentLine,
        column: isTween ? 0 : i,
        service
      });

      Object.entries(service.relationships).forEach(([, relationship]) => {
        const endpointName =
          typeof relationship === "string"
            ? relationship.split(":", 1)[0]
            : relationship?.service;

        s.children.push(endpointName);
      });

      Object.entries(currentDeployment?.workers).forEach(
        ([workerName, worker]) => {
          Object.entries(worker.relationships).forEach(([, relationship]) => {
            const endpointName =
              typeof relationship === "string"
                ? relationship.split(":", 1)[0]
                : relationship?.service;
            if (endpointName === name) {
              s.children.push(workerName);
              s.metadata.workers.push(worker);
            }
          });

          // Checks if any worker on the app is connected to the service.
          // Since services are positioned above the workers, we need to link them
          // in this way. Linking a worker to a service will make the worker draw
          // a line to the service from the bottom of the worker.
          const mounts = Object.values(worker.mounts).map(
            mount => mount.service
          );
          if (mounts.includes(name)) {
            s.children.push(workerName);
          }
        }
      );

      treeGraph.push(s);
    });

    currentLine++;

    Object.entries(currentDeployment.workers ?? {}).forEach(
      ([name, worker], i) => {
        const [type] = worker.type.split(":", 1);

        const w = getDeploymentWorkers({
          name,
          type,
          currentLine,
          column: i,
          worker
        });
        treeGraph.push(w);
      }
    );
    return treeGraph;
  }, [currentDeployment, autoscalingConfiguration, environmentId]);

  // If a projects name starts with a number then all the workers and the webapp will have a class associated with them starting with the same number as the name of the project. This will cause a bug where the class cannot be retrieved and the "onMouseover" function will not work. This will prevent the path from being highlighted and the svg tooltip from displaying.
  //  The solution is to append "node-" to any matching "id" property
  serviceGraphTree.forEach(node => {
    node.id = sanitizeId(node.id);
    if (node?.children) {
      node.children = node.children.map(childNode => sanitizeId(childNode));
    }
  });

  return serviceGraphTree;
};
